import { useTranslation } from "@privacy/translations";
import Stack from "@mui/material/Stack";
import type { SxProps, Theme } from "@mui/material";
import { Box, Typography } from "@mui/material";
import { Drawer } from "../../../shared/Drawer";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import {
  useGetDpiaRecommendedForm,
  useGetRegistrationById,
  useUpdateDpiaRecommendedAnswers,
} from "../../../api/registrations";
import { DynamicGrid } from "../../../dynamicGrid";
import { DpiaIcon1, AlertTriangleIcon, TriangleAlertIcon } from "../../../shared/icons";
import { DynamicForm } from "../../../dynamicForm";
import { usePermission } from "../../../shared/permissions";
import type { RegistrationById } from "../../../api/registrations.types";
import { HACK_alignIconWithLabelBox } from "@privacy/ui";
import { RegistrationDrawerHeader } from "./RegistrationDrawerHeader";
import { useRegistrationVariant } from "../RegistrationVariantProvider";
import { useHasPermissionToUpdateAnswers } from "./useHasPermissionToUpdate";

export const DpiaRecommendedSubDrawer = () => {
  const registrationId = DynamicGrid.useSelectedRowId();
  const variant = useRegistrationVariant();
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const hasPermissionToUpdateAnswers = useHasPermissionToUpdateAnswers();
  const { data: registration } = useGetRegistrationById(registrationId, variant);
  const { navigate } = usePrivacyNavigate();
  const dpiaRecommendedFormQuery = useGetDpiaRecommendedForm(variant);
  const {
    mutateAsync: updateDpiaRecommendedAnswers,
    isError: isSaveError,
    isPending,
  } = useUpdateDpiaRecommendedAnswers();

  const isFormReadOnly = !hasPermission("nova-forms-api:registration:update");

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!registration || !dpiaRecommendedFormQuery.data) return <Drawer.Loading />;

        return (
          <DynamicForm.Provider
            onSave={answer => updateDpiaRecommendedAnswers({ registrationId: registration.id, answers: [answer] })}
            fileOwner={{ type: "RegistrationAutoDpiaPreAssessment", id: registration.id }}
            isSaving={isPending}
            isReadOnly={isFormReadOnly || !hasPermissionToUpdateAnswers(registration)}
            isSaveError={isSaveError}
            key={registration.autoDPIAPreAssessment?.id ?? `no-id-${registration.id}`}
            answers={registration.autoDPIAPreAssessment?.answers ?? []}
          >
            {({ formState }) => (
              <>
                <Stack sx={{ height: "100%" }}>
                  <RegistrationDrawerHeader
                    activeTab="standard"
                    statusIndicator={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                    registration={registration}
                    subDrawerHeader={
                      <Drawer.SubDrawerHeader
                        onBackClick={() => navigate("..")}
                        renderIcon={props => <DpiaIcon1 {...props} />}
                        title={
                          <DpiaRecommendedSubDrawerTitle
                            sx={theme => ({ fontSize: theme.typography.pxToRem(16) })}
                            autoDPIAPreAssessment={registration.autoDPIAPreAssessment}
                          />
                        }
                      />
                    }
                  />
                  <Stack spacing={4} sx={{ px: Drawer.PADDING_X, pt: 3 }}>
                    <DynamicForm.InlineForm id={registration.id} form={dpiaRecommendedFormQuery.data} />
                    {/* This checks if the risk criteria is equal to 2 or more then we show the recommended DPIA and criteria */}
                    {registration.autoDPIAPreAssessment &&
                      registration.autoDPIAPreAssessment.riskCriteria.length > 1 && (
                        <Stack
                          sx={theme => ({
                            width: "100%",
                            bgcolor: theme.palette.brandOrange["50"],
                            p: 3,
                            borderRadius: 1,
                            fontSize: theme.typography.pxToRem(14),
                            border: `1px solid ${theme.palette.brandOrange["200"]}`,
                          })}
                        >
                          <Typography sx={{ fontSize: "inherit", fontWeight: "600" }}>
                            {t("RegistrationDrawer.Dpia.Recommended.Title")}
                          </Typography>
                          <Typography sx={{ fontSize: "inherit" }}>
                            {t("RegistrationDrawer.Dpia.Recommended.Body")}
                          </Typography>
                          <Stack sx={{ pt: 2, gap: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "inherit",
                                fontWeight: "600",
                                color: theme => theme.palette.text.secondary,
                              }}
                            >
                              {t("RegistrationDrawer.Dpia.RiskCriteria.Title")}
                            </Typography>
                            {registration.autoDPIAPreAssessment.riskCriteria.map(riskCriteria => (
                              <Stack key={riskCriteria} direction="row" sx={{ alignItems: "center", gap: 1 }}>
                                <TriangleAlertIcon size={16} color={theme => theme.palette.brandOrange["500"]} />
                                <HACK_alignIconWithLabelBox>
                                  <Typography
                                    sx={{ fontSize: "inherit", color: theme => theme.palette.text.secondary }}
                                  >
                                    {riskCriteria}
                                  </Typography>
                                </HACK_alignIconWithLabelBox>
                              </Stack>
                            ))}
                          </Stack>
                        </Stack>
                      )}
                  </Stack>
                </Stack>
              </>
            )}
          </DynamicForm.Provider>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

export const DpiaRecommendedSubDrawerTitle: React.FC<{
  autoDPIAPreAssessment: RegistrationById["autoDPIAPreAssessment"];
  sx: SxProps<Theme>;
}> = ({ autoDPIAPreAssessment, sx }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography
        sx={[
          {
            fontWeight: "600",
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {t("RegistrationDrawer.Dpia")}{" "}
        {autoDPIAPreAssessment?.dpiaStatus && (
          <Box component="span" sx={{ fontWeight: "400" }}>
            {t(`RegistrationDrawer.Dpia.${autoDPIAPreAssessment.dpiaStatus}`)}
          </Box>
        )}
      </Typography>
      {autoDPIAPreAssessment?.dpiaStatus && autoDPIAPreAssessment.dpiaStatus === "recommended" && <AlertTriangleIcon />}
    </Box>
  );
};
